import React, { useState } from 'react';
import CitationGroup from '../components/CitationChip';
import { useChatUi } from '../ChatContext';

export const DTC_RE = /^[PBCU][0-9A-F]{4}(-[0-9A-F]{2})?$/;
const WARNING_RE = /^(警告|危険|Warning|WARNING|Danger|DANGER)/;
const CAUTION_RE = /^(注意|注記|Caution|CAUTION|Note|NOTE|Important)/;

/** Concatenated text of a hast node. */
export function hastText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.value || '';
  return (node.children || []).map(hastText).join('');
}

function firstStrongText(node) {
  if (!node || !node.children) return '';
  for (const child of node.children) {
    if (child.type === 'element' && child.tagName === 'strong') return hastText(child).trim();
    if (child.type === 'element') {
      const found = firstStrongText(child);
      if (found) return found;
    }
    if (child.type === 'text' && child.value.trim()) return '';
  }
  return '';
}

function CodeBlock({ node }) {
  const { t } = useChatUi();
  const [copied, setCopied] = useState(false);
  const code = node && node.children && node.children[0];
  const text = hastText(code).replace(/\n$/, '');
  const cls = (code && code.properties && code.properties.className) || [];
  const lang = (Array.isArray(cls) ? cls : [cls]).map(String).find((c) => c.startsWith('language-'));
  const language = lang ? lang.slice('language-'.length) : '';

  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="cv-code">
      <div className="cv-code__bar">
        <span className="cv-code__lang">{language || 'text'}</span>
        <button type="button" className="cv-code__copy" onClick={copy}>{copied ? t('Copied') : t('Copy')}</button>
      </div>
      <pre><code>{text}</code></pre>
    </div>
  );
}

function Blockquote({ node, children }) {
  const lead = firstStrongText(node);
  let variant = null;
  if (WARNING_RE.test(lead)) variant = 'warning';
  else if (CAUTION_RE.test(lead)) variant = 'caution';
  if (!variant) return <blockquote className="cv-quote">{children}</blockquote>;
  return (
    <aside className={`cv-callout cv-callout--${variant}`} role="note">
      <span className="cv-callout__icon" aria-hidden="true">
        {variant === 'warning' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        )}
      </span>
      <div className="cv-callout__body">{children}</div>
    </aside>
  );
}

const strip = ({ node, ...rest }) => rest; // never pass the hast node down to DOM elements

/** react-markdown component map shared by every block. */
export const markdownComponents = {
  table: ({ node, ...props }) => (
    <div className="tableWrap cv-table-wrap" tabIndex={0}>
      <table {...props} />
    </div>
  ),
  code: ({ node, className, children, ...props }) => {
    const text = String(children == null ? '' : children);
    if (DTC_RE.test(text.trim())) return <code className="dtc" {...props}>{children}</code>;
    return <code className="code" {...props}>{children}</code>;
  },
  pre: CodeBlock,
  blockquote: Blockquote,
  a: ({ node, href, children, ...props }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
  img: () => null,
  hr: () => null,
  // the answers use "###"; larger headings are rendered at the same level
  h1: ({ children, ...props }) => <h3 {...strip(props)}>{children}</h3>,
  h2: ({ children, ...props }) => <h3 {...strip(props)}>{children}</h3>,
  'cite-ref': ({ ids }) => <CitationGroup ids={ids} />,
};
